import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ApiError } from '../lib/api';
import { compact, money0, pct, titleCase } from '../lib/format';

/* ---------------- toasts ---------------- */

type ToastKind = 'info' | 'ok' | 'err';
interface Toast {
  id: number;
  message: string;
  kind: ToastKind;
}

const ToastContext = createContext<{
  push: (message: string, kind?: ToastKind) => void;
  fail: (error: unknown) => void;
} | null>(null);

/** "business.pincode" reads as "PIN code" to somebody looking at a form. */
const LABELS: Record<string, string> = {
  pincode: 'PIN code',
  gstin: 'GSTIN',
  fssai: 'FSSAI',
  ifsc: 'IFSC',
  monthlyGross: 'Monthly gross',
  dailyWage: 'Daily wage',
  bankAccount: 'Account number',
  dateOfJoining: 'Date of joining',
  acceptTerms: 'Terms',
  otp: 'Verification code',
};

const fieldLabel = (path: string): string => {
  const field = path.split('.').pop() ?? path;
  return LABELS[field] ?? titleCase(field.replace(/([A-Z])/g, ' $1').trim());
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((message: string, kind: ToastKind = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, message, kind }]);
    setTimeout(
      () => setToasts((current) => current.filter((t) => t.id !== id)),
      kind === 'err' ? 5000 : 2800,
    );
  }, []);

  const fail = useCallback(
    (error: unknown) => {
      /*
       * A validation failure carries the field that failed, so say that
       * instead of "check the highlighted fields" - which is no help at all on
       * a screen that does not highlight anything.
       */
      const detail =
        error instanceof ApiError && Array.isArray(error.details) ? error.details[0] : undefined;

      const message = detail
        ? `${fieldLabel(detail.path)}: ${detail.message}`
        : error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : 'Something went wrong';
      push(message, 'err');
    },
    [push],
  );

  const value = useMemo(() => ({ push, fail }), [push, fail]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast ${toast.kind === 'info' ? '' : toast.kind}`}>
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast must be used inside <ToastProvider>');
  return value;
}

/* ---------------- overlays ---------------- */

interface OverlayProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'narrow' | 'wide' | '';
}

function useEscape(onClose: () => void) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);
}

export function Modal({ title, onClose, children, footer, size = '' }: OverlayProps) {
  useEscape(onClose);
  return createPortal(
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${size}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="btn ghost icon sm" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

export function Drawer({ title, onClose, children, footer, size = '' }: OverlayProps) {
  useEscape(onClose);
  return createPortal(
    <div className="drawer-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`drawer ${size}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="btn ghost icon sm" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="modal-body grow">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmDialog({
  title = 'Please confirm',
  message,
  confirmLabel = 'Confirm',
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
}: {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal
      title={title}
      size="narrow"
      onClose={onCancel}
      footer={
        <>
          <button className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            className={`btn ${danger ? 'danger' : 'primary'}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      <p className="dim">{message}</p>
    </Modal>
  );
}

/* ---------------- form fields ---------------- */

interface FieldProps {
  label: string;
  /** Node, not just text: a hint is sometimes a link or a small action. */
  hint?: ReactNode;
  error?: string;
  children: ReactNode;
  className?: string;
}

export function Field({ label, hint, error, children, className = '' }: FieldProps) {
  return (
    <div className={`field ${className}`}>
      <label>{label}</label>
      {children}
      {error ? (
        <span className="field-error">{error}</span>
      ) : hint ? (
        <span className="hint">{hint}</span>
      ) : null}
    </div>
  );
}

type TextInputProps = React.InputHTMLAttributes<HTMLInputElement> & Omit<FieldProps, 'children'>;

export function TextInput({ label, hint, error, className, ...rest }: TextInputProps) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} className={className ?? ''}>
      <input id={id} className="input" {...rest} />
    </Field>
  );
}

export function Select({
  label,
  hint,
  error,
  className,
  children,
  ...rest
}: React.SelectHTMLAttributes<HTMLSelectElement> &
  Omit<FieldProps, 'children'> & { children: ReactNode }) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} className={className ?? ''}>
      <select id={id} className="select" {...rest}>
        {children}
      </select>
    </Field>
  );
}

/* ---------------- data display ---------------- */

export function Kpi({
  label,
  value,
  foot,
  tone = '',
}: {
  label: string;
  value: ReactNode;
  foot?: ReactNode;
  tone?: '' | 'amber' | 'red' | 'blue';
}) {
  return (
    <div className={`kpi ${tone}`}>
      <div className="k-label">{label}</div>
      <div className="k-value">{value}</div>
      {foot ? <div className="k-foot">{foot}</div> : null}
    </div>
  );
}

const CHIP_TONES: Record<string, string> = {
  VACANT: 'ok',
  OCCUPIED: 'info',
  DIRTY: 'warn',
  OUT_OF_ORDER: 'danger',
  PAID: 'ok',
  POSTED_TO_ROOM: 'info',
  VOID: 'danger',
  OPEN: 'warn',
  BILLED: 'ok',
  DRAFT: '',
  PENDING_APPROVAL: 'warn',
  APPROVED: 'info',
  PARTIALLY_RECEIVED: 'warn',
  RECEIVED: 'ok',
  REJECTED: 'danger',
  CANCELLED: 'danger',
  FINALIZED: 'ok',
  IN_HOUSE: 'info',
  CHECKED_OUT: '',
  OK: 'ok',
  LOW_MARGIN: 'danger',
  HEADROOM: 'info',
  NO_RECIPE: 'warn',
  UNPRICED: 'warn',
  PENDING: 'warn',
  IN_PROGRESS: 'info',
  DONE: 'ok',
  BLOCKED: 'danger',
  BOOKED: 'info',
  CONFIRMED: 'ok',
  SEATED: 'brand',
  COMPLETED: '',
  NO_SHOW: 'danger',
  QUEUED: '',
  PREPARING: 'warn',
  READY: 'ok',
  SERVED: '',
  PRESENT: 'ok',
  ABSENT: 'danger',
  HALF_DAY: 'warn',
  PAID_LEAVE: 'info',
  WEEKLY_OFF: '',
  TRIALING: 'info',
  ACTIVE: 'ok',
  PAST_DUE: 'warn',
  SUSPENDED: 'danger',
  EXPIRED: 'danger',
};

export const StatusChip = ({ status }: { status: string }) => (
  <span className={`chip ${CHIP_TONES[status] ?? ''}`}>{titleCase(status)}</span>
);

export const Meter = ({ value, tone = '' }: { value: number; tone?: '' | 'warn' | 'danger' }) => (
  <div className={`meter ${tone}`}>
    <span style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
  </div>
);

export const Empty = ({
  icon = '∅',
  message,
  action,
}: {
  icon?: string;
  message: string;
  action?: ReactNode;
}) => (
  <div className="empty">
    <span className="big">{icon}</span>
    {message}
    {action ? <div style={{ marginTop: 12 }}>{action}</div> : null}
  </div>
);

export const Loading = ({ label = 'Loading…' }: { label?: string }) => (
  <div className="loading-block">
    <span className="spinner" />
    {label}
  </div>
);

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : 'Something went wrong';
  return (
    <div className="empty">
      <span className="big">⚠</span>
      {message}
      {onRetry ? (
        <div style={{ marginTop: 12 }}>
          <button className="btn" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : null}
    </div>
  );
}

/* ---------------- charts ---------------- */

export function BarChart({
  data,
  height = 190,
  stacked = false,
}: {
  data: Array<{ label: string; value: number; stack?: number }>;
  height?: number;
  stacked?: boolean;
}) {
  if (data.length === 0) return <Empty message="No data for this period" />;

  const max = Math.max(...data.map((d) => d.value + (stacked ? (d.stack ?? 0) : 0)), 1);
  const slot = 100 / data.length;
  const barWidth = Math.min(slot * 0.56, 8);
  const plot = height - 26;

  return (
    <svg
      className="chart"
      viewBox={`0 0 100 ${height}`}
      preserveAspectRatio="none"
      style={{ height }}
    >
      {[0.25, 0.5, 0.75, 1].map((g) => (
        <line
          key={g}
          className="grid-line"
          x1="0"
          x2="100"
          y1={plot * (1 - g)}
          y2={plot * (1 - g)}
          strokeWidth="0.15"
        />
      ))}
      {data.map((d, i) => {
        const h1 = (d.value / max) * plot;
        const h2 = stacked ? ((d.stack ?? 0) / max) * plot : 0;
        const x = i * slot + slot / 2 - barWidth / 2;
        return (
          <g key={`${d.label}-${i}`} className="bar">
            <title>{`${d.label}: ${money0(d.value + (d.stack ?? 0))}`}</title>
            {stacked ? (
              <rect
                x={x}
                y={plot - h1 - h2}
                width={barWidth}
                height={Math.max(h2, 0)}
                fill="var(--accent)"
                rx="1"
              />
            ) : null}
            <rect
              x={x}
              y={plot - h1}
              width={barWidth}
              height={Math.max(h1, 0.4)}
              fill="var(--brand-500)"
              rx="1"
            />
          </g>
        );
      })}
      {data.map((d, i) => (
        <text
          key={`l-${d.label}-${i}`}
          x={i * slot + slot / 2}
          y={height - 8}
          textAnchor="middle"
          style={{ fontSize: 3.4 }}
        >
          {d.label}
        </text>
      ))}
    </svg>
  );
}

export function BarList({
  rows,
  format = money0,
}: {
  rows: Array<{ label: string; value: number }>;
  format?: (value: number) => string;
}) {
  if (rows.length === 0) return <Empty message="Nothing to show yet" />;
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div className="col gap-6" style={{ padding: '4px 0' }}>
      {rows.map((row) => (
        <div key={row.label}>
          <div className="row between small">
            <span>{row.label}</span>
            <span className="b num">{format(row.value)}</span>
          </div>
          <div className="meter" style={{ marginTop: 4 }}>
            <span style={{ width: `${(row.value / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function Donut({
  value,
  label,
  size = 96,
}: {
  value: number;
  label?: string;
  size?: number;
}) {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
      <circle cx="50" cy="50" r={radius} fill="none" stroke="var(--line-2)" strokeWidth="11" />
      <circle
        cx="50"
        cy="50"
        r={radius}
        fill="none"
        stroke="var(--brand-500)"
        strokeWidth="11"
        strokeDasharray={`${(clamped / 100) * circumference} ${circumference}`}
        strokeLinecap="round"
        transform="rotate(-90 50 50)"
      />
      <text
        x="50"
        y="49"
        textAnchor="middle"
        style={{ fontSize: 19, fontWeight: 700, fill: 'var(--text)' }}
      >
        {pct(clamped).replace('.0', '')}
      </text>
      {label ? (
        <text x="50" y="64" textAnchor="middle" style={{ fontSize: 9, fill: 'var(--muted)' }}>
          {label}
        </text>
      ) : null}
    </svg>
  );
}

export const Compact = ({ value }: { value: number }) => <>{compact(value)}</>;
